import React, { useEffect, useState } from 'react';
import { Row, Col, Button, Input, FormGroup, Label, Table } from 'reactstrap';
import { axiosApi } from '../Basicos/axiosInstances';
import { dateToPtBr } from '../Basicos/funcoes';
import { ROTULOS_SITUACAO, CORES_SITUACAO } from './vacinacaoConstantes';

const ROTULOS_MOTIVO_RECUSA = {
    nao_e_pdf_oficial: 'Não é o PDF emitido pelo sistema (parece foto)',
    tipo_incompativel: 'Tipo escolhido não corresponde ao conteúdo',
    protegido_por_senha: 'PDF protegido por senha',
    nenhuma_vacina_encontrada: 'Nenhuma vacina exigida encontrada',
};

// Agrupa por vacina_id (não por tipo_vacina_exigida_id) — é a mesma identidade estável que o
// backend usa pra calcular conformidade (inclusive histórico de campanhas/semestres anteriores,
// que nem sempre carrega o tipo_vacina_exigida_id da janela atual). Um registro de ajuste manual
// (origem = revisao_manual) representa "N doses confirmadas" no campo dose, não uma dose isolada
// — por isso soma o valor de dose, em vez de contar 1 por registro, igual ao contarDoses() do backend.
function agruparIdentificadas(vacinasDoAluno) {
    const porVacina = {};

    (vacinasDoAluno || []).forEach((vacina) => {
        if (!vacina.vacina_id) {
            return;
        }
        if (!porVacina[vacina.vacina_id]) {
            porVacina[vacina.vacina_id] = { doses: 0, ultimaDose: null };
        }
        porVacina[vacina.vacina_id].doses += vacina.origem === 'revisao_manual' ? Number(vacina.dose) || 0 : 1;
        if (!porVacina[vacina.vacina_id].ultimaDose || vacina.data_aplicacao > porVacina[vacina.vacina_id].ultimaDose) {
            porVacina[vacina.vacina_id].ultimaDose = vacina.data_aplicacao;
        }
    });

    return porVacina;
}

function situacaoDaLinha(linha) {
    const identificado = Number(linha.doses_identificadas) || 0;

    if (identificado === 0) {
        return { texto: 'Não encontrada', cor: '#dc3545' };
    }
    if (identificado < linha.exigido) {
        return { texto: `Falta ${linha.exigido - identificado} dose${linha.exigido - identificado > 1 ? 's' : ''}`, cor: '#dc3545' };
    }
    return { texto: 'Atende', cor: '#28a745' };
}

export default function AnaliseVacinacao({ campanhaId, alunoId, onVoltar }) {
    const [carregando, setCarregando] = useState(true);
    const [dados, setDados] = useState(null);
    const [linhas, setLinhas] = useState([]);
    const [motivo, setMotivo] = useState('');
    const [documentoSelecionado, setDocumentoSelecionado] = useState(null);
    const [confirmacaoAberta, setConfirmacaoAberta] = useState(false);
    const [decisao, setDecisao] = useState(null);
    const [tipoDocumentoId, setTipoDocumentoId] = useState('');
    const [arquivo, setArquivo] = useState(null);
    const [enviandoDocumento, setEnviandoDocumento] = useState(false);
    const [erroUpload, setErroUpload] = useState('');

    const carregarDados = async () => {
        if (!campanhaId || !alunoId) {
            return;
        }

        setCarregando(true);
        try {
            const { data } = await axiosApi.get(`/vacinacao/analise/${campanhaId}/${alunoId}`);
            setDados(data);

            const agrupadas = agruparIdentificadas(data.vacinas_do_aluno);
            setLinhas(
                (data.vacinas_exigidas || []).map((exigida) => ({
                    tipo_vacina_exigida_id: exigida.id,
                    nome: exigida.nome,
                    exigido: exigida.doses_necessarias,
                    doses_identificadas: agrupadas[exigida.vacina_id]?.doses ?? 0,
                    ultima_dose: agrupadas[exigida.vacina_id]?.ultimaDose ?? '',
                }))
            );

            const primeiroAceito = (data.documentos || []).find((documento) => documento.status_leitura === 'aceito');
            setDocumentoSelecionado(primeiroAceito || null);
        } catch (error) {
            console.log(error);
        } finally {
            setCarregando(false);
        }
    };

    useEffect(() => {
        carregarDados();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campanhaId, alunoId]);

    const enviarDocumento = async () => {
        if (!tipoDocumentoId || !arquivo) {
            return;
        }

        setEnviandoDocumento(true);
        setErroUpload('');

        const formData = new FormData();
        formData.append('tipo_documento_vacinacao_id', tipoDocumentoId);
        formData.append('arquivo', arquivo);

        try {
            await axiosApi.post(`/vacinacao/analise/${campanhaId}/${alunoId}/documento`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setTipoDocumentoId('');
            setArquivo(null);
            await carregarDados();
        } catch (error) {
            setErroUpload(error.response?.data?.erro || 'Não foi possível enviar o documento.');
        } finally {
            setEnviandoDocumento(false);
        }
    };

    const atualizarLinha = (index, campo, valor) => {
        setLinhas((atual) => atual.map((linha, i) => (i === index ? { ...linha, [campo]: valor } : linha)));
    };

    const pedirConfirmacao = (conforme) => {
        setDecisao(conforme);
        setConfirmacaoAberta(true);
    };

    const confirmarParecer = async () => {
        try {
            await axiosApi.put(`/vacinacao/analise/${campanhaId}/${alunoId}/revisar`, {
                conforme: decisao,
                motivo: decisao ? null : motivo,
                ajustes: linhas.map((linha) => ({
                    tipo_vacina_exigida_id: linha.tipo_vacina_exigida_id,
                    doses_identificadas: Number(linha.doses_identificadas),
                    ultima_dose: linha.ultima_dose || null,
                })),
            });
            setConfirmacaoAberta(false);
            onVoltar(true);
        } catch (error) {
            console.log(error);
        }
    };

    if (carregando || !dados) {
        return (
            <Col sm={12} md={12} className="col-conteudo p-4">
                <p>Carregando...</p>
            </Col>
        );
    }

    const situacaoAtual = dados.situacao_atual?.situacao || 'pendente_revisao_manual';

    return (
        <Col sm={12} md={12} className="col-conteudo p-4">
            <div className="mb-2">
                <a href="#voltar" onClick={(e) => { e.preventDefault(); onVoltar(false); }}>Vacinação - Análise do aluno</a>
            </div>

            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 style={{ color: 'var(--cor-principal)', fontWeight: 700 }}>Análise da Vacinação</h4>
                <Button color="primary" outline onClick={() => onVoltar(false)}>Voltar</Button>
            </div>

            <div className="d-flex justify-content-between align-items-center mb-4" style={{ border: '1px solid #e9ecef', borderRadius: '.5rem', padding: '1rem' }}>
                <div>
                    <h5 className="mb-1"><b>{dados.aluno?.nome}</b></h5>
                    <span style={{ color: '#6c757d' }}>
                        Matrícula {dados.aluno?.matricula} · {dados.aluno?.curso_descricao} · {dados.aluno?.semestre}º semestre · Grupo {dados.aluno?.grupo}
                    </span>
                </div>
                <span
                    className="px-3 py-1"
                    style={{
                        borderRadius: '1rem',
                        fontWeight: 700,
                        color: CORES_SITUACAO[situacaoAtual],
                        backgroundColor: `${CORES_SITUACAO[situacaoAtual]}22`,
                    }}
                >
                    {ROTULOS_SITUACAO[situacaoAtual]}
                </span>
            </div>

            <Row>
                <Col md={5}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0"><b>Documentos enviados</b></h6>
                    </div>

                    <div className="mb-3 p-2" style={{ border: '1px dashed #ced4da', borderRadius: '.5rem' }}>
                        <Label className="mb-1" style={{ fontSize: '.85rem', fontWeight: 600 }}>Enviar documento em nome do aluno</Label>
                        <Row form>
                            <Col md={6} className="mb-2">
                                <Input
                                    type="select"
                                    bsSize="sm"
                                    value={tipoDocumentoId}
                                    onChange={(e) => setTipoDocumentoId(e.target.value)}
                                >
                                    <option value="">Tipo de documento...</option>
                                    {(dados.tipos_documento || []).map((tipo) => (
                                        <option key={tipo.id} value={tipo.id}>{tipo.descricao}</option>
                                    ))}
                                </Input>
                            </Col>
                            <Col md={6} className="mb-2">
                                <Input
                                    type="file"
                                    bsSize="sm"
                                    accept="application/pdf"
                                    onChange={(e) => setArquivo(e.target.files[0] || null)}
                                />
                            </Col>
                        </Row>
                        <Button
                            size="sm"
                            color="primary"
                            disabled={!tipoDocumentoId || !arquivo || enviandoDocumento}
                            onClick={enviarDocumento}
                        >
                            {enviandoDocumento ? 'Enviando...' : 'Enviar'}
                        </Button>
                        {erroUpload && <div className="mt-2" style={{ color: '#dc3545', fontSize: '.85rem' }}>{erroUpload}</div>}
                    </div>

                    {(dados.documentos || []).map((documento) => (
                        <div
                            key={documento.id}
                            onClick={() => setDocumentoSelecionado(documento)}
                            style={{
                                border: documentoSelecionado?.id === documento.id ? '2px solid var(--cor-principal)' : '1px solid #e9ecef',
                                borderRadius: '.5rem',
                                padding: '.75rem',
                                marginBottom: '.75rem',
                                cursor: 'pointer',
                            }}
                        >
                            <div className="d-flex justify-content-between align-items-center">
                                <b>{documento.nome}</b>
                                <span
                                    className="px-2"
                                    style={{
                                        borderRadius: '1rem',
                                        fontSize: '.75rem',
                                        fontWeight: 700,
                                        color: documento.status_leitura === 'aceito' ? '#28a745' : '#dc3545',
                                        backgroundColor: documento.status_leitura === 'aceito' ? '#28a74522' : '#dc354522',
                                    }}
                                >
                                    {documento.status_leitura === 'aceito' ? 'Aceito' : 'Recusado'}
                                </span>
                            </div>
                            <div style={{ color: '#6c757d', fontSize: '.85rem' }}>{documento.tipo_documento_descricao}</div>
                            <div style={{ color: '#6c757d', fontSize: '.8rem' }}>
                                Enviado em {dateToPtBr(documento.created_at?.slice(0, 10))}
                                {documento.status_leitura === 'aceito' && (
                                    ` · ${documento.vacinas_identificadas_count} vacinas, ${documento.doses_identificadas_count} doses`
                                )}
                            </div>
                            {documento.status_leitura === 'recusado' && (
                                <div className="mt-2 p-2" style={{ background: '#fdecea', color: '#dc3545', borderRadius: '.35rem', fontSize: '.85rem' }}>
                                    Motivo da recusa: {ROTULOS_MOTIVO_RECUSA[documento.motivo_recusa] || documento.motivo_recusa}
                                </div>
                            )}
                        </div>
                    ))}

                    <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ border: '1px solid #e9ecef', borderRadius: '.5rem', minHeight: 320, color: '#adb5bd' }}
                    >
                        {documentoSelecionado?.status_leitura === 'aceito' ? (
                            <iframe
                                title="Pré-visualização do PDF"
                                src={documentoSelecionado.arquivo_url}
                                style={{ width: '100%', height: 320, border: 'none' }}
                            />
                        ) : 'Pré-visualização do PDF'}
                    </div>
                </Col>

                <Col md={7}>
                    <h6 className="mb-3"><b>Vacinas exigidas × identificadas</b></h6>
                    <Table size="sm" className="tabelaConsulta">
                        <thead>
                            <tr>
                                <th className="tabelaConsulta__celulaCabecalho">Vacina</th>
                                <th className="tabelaConsulta__celulaCabecalho">Exigido</th>
                                <th className="tabelaConsulta__celulaCabecalho">Identificado</th>
                                <th className="tabelaConsulta__celulaCabecalho">Última dose</th>
                                <th className="tabelaConsulta__celulaCabecalho">Situação</th>
                            </tr>
                        </thead>
                        <tbody>
                            {linhas.map((linha, index) => {
                                const situacaoLinha = situacaoDaLinha(linha);
                                return (
                                    <tr key={linha.tipo_vacina_exigida_id}>
                                        <td className="tabelaConsulta__celulaDados">{linha.nome}</td>
                                        <td className="tabelaConsulta__celulaDados">{linha.exigido}</td>
                                        <td className="tabelaConsulta__celulaDados">
                                            <Input
                                                type="number"
                                                min="0"
                                                bsSize="sm"
                                                value={linha.doses_identificadas}
                                                onChange={(e) => atualizarLinha(index, 'doses_identificadas', e.target.value)}
                                            />
                                        </td>
                                        <td className="tabelaConsulta__celulaDados">
                                            <Input
                                                type="date"
                                                bsSize="sm"
                                                value={linha.ultima_dose || ''}
                                                onChange={(e) => atualizarLinha(index, 'ultima_dose', e.target.value)}
                                            />
                                        </td>
                                        <td className="tabelaConsulta__celulaDados" style={{ color: situacaoLinha.cor, fontWeight: 700 }}>
                                            {situacaoLinha.texto}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </Col>
            </Row>

            <FormGroup className="mt-3">
                <Label>Observações enviadas ao aluno</Label>
                <Input type="textarea" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
            </FormGroup>

            <div className="d-flex justify-content-end">
                <Button color="primary" outline className="mr-2" onClick={() => pedirConfirmacao(false)} disabled={!motivo}>Não conforme</Button>
                <Button color="primary" onClick={() => pedirConfirmacao(true)}>Conforme</Button>
            </div>

            {confirmacaoAberta && (
                <div
                    className="d-flex align-items-center justify-content-center"
                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,.5)', zIndex: 1050 }}
                >
                    <div style={{ background: '#fff', borderRadius: '.5rem', padding: '1.5rem', maxWidth: 480 }}>
                        <h5 className="text-center mb-3"><b>Confirmar parecer</b></h5>
                        <p>
                            {dados.aluno?.nome} receberá um e-mail informando que a comprovação {decisao ? 'foi confirmada' : 'não foi confirmada'}
                            {!decisao && ', com as observações abaixo.'}
                        </p>
                        {!decisao && (
                            <div className="p-2 mb-3" style={{ background: '#f0f0f0', borderRadius: 6 }}>{motivo}</div>
                        )}
                        <div className="d-flex justify-content-end">
                            <Button color="primary" outline className="mr-2" onClick={() => setConfirmacaoAberta(false)}>Cancelar</Button>
                            <Button color="primary" onClick={confirmarParecer}>Confirmar</Button>
                        </div>
                    </div>
                </div>
            )}
        </Col>
    );
}
